// Import dependencies
import { unsafeCSS, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { AutomatorLogItem } from '@pages/new-logs/components/log-item/_base';
import { isEmpty, htmlEntities } from '@automator/utilities';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logItemLoop.i18n;

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop-action' )
export class AutomatorLoopAction extends AutomatorLogItem {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The status has a different format if it's inside a loop
	@property( { type: Array, attribute: false } ) accessor status = [];

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if the data is missing
		if ( this.isDataMissing ) {
			return this._templateMissingData;
		}

		return html`
			<div class="item">
				${ this._templateIntegrationIcon }
				${ this._templateSentence }
				${ this._templateHighlightedStatuses }
				${ this._templateOpenSidebarButton }
			</div>
		`;
	}

	/**
	 * Renders a container that shows the highlighted statuses, like the errors
	 *
	 * @return {TemplateResult|undefined} The highlighted statuses
	 */
	get _templateHighlightedStatuses() {
		// The statuses we'll show in this summary
		const highlightedStatuses = [ 'completed-with-errors', 'skipped', 'in-progress' ];

		// Get the highlighted statuses
		const statuses = this.status.filter( statusGroup => highlightedStatuses.includes( statusGroup.status_id ) );

		if ( statuses.length === 0 ) {
			return;
		}

		return html`
			<div class="item-loop__highlighted-statuses">
				${ repeat(
					statuses,
					statusGroup => statusGroup.status_id,
					statusGroup => html`
						<uap-log-status 
							status-id=${ statusGroup.status_id }
							count-number=${ statusGroup.count }
							hide-label
							show-tooltip
						></uap-log-status>
					`
				) }
			</div>
		`;
	}

	/**
	 * Creates the object passed to the sidebar
	 *
	 * @return {Object} The sidebar content
	 */
	get sidebarContent() {
		return {
			title: strings.actionInsideTitle,
			summary: this.sidebarSummary,
			runs: this.sidebarRuns,
			miscellaneous: {
				/**
				 * When there is only a single run, Automator will typically hide the run's summary and attempt
				 * to display only the properties, which usually pertains to the fields. However, for actions
				 * executed within loops, it is necessary to always display the summary of each run, regardless
				 * of whether there is only one run.
				 *
				 * @type {Boolean} - Whether to always show the run's summary
				 */
				runsAlwaysShowSummaries: true
			}
		}
	}

	/**
	 * Creates the summary for the sidebar
	 *
	 * @return {Object} Summary for the sidebar
	 */
	get sidebarSummary() {
		// Summary items
		const items = [];

		// Add status
		items.push( {
			label: strings.summary.status,
			value: repeat(
				this.status,
				status => status.status_id,
				status => html`
					<div>
						<uap-log-status 
							status-id=${ status.status_id }
							count-number=${ status.count }
						></uap-log-status>
					</div>
				`
			)
		} );

		return {
			title: html`
				<uap-log-integration-icon integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
				<uap-log-sentence .sentence="${ this.titleHTML }"></uap-log-sentence>
			`,
			items: items
		}
	}

	/**
	 * Creates the array for the sidebar to render the action runs
	 *
	 * @return {Array} The array of runs
	 */
	get sidebarRuns() {
		if ( this.runs.length === 0 ) {
			return [];
		}

		const runs = [];

		// Runs are grouped in actions inside loops. For now, remove them from their groups
		this.runs.forEach( runGroup => {
			// Iterate each run inside
			runGroup.runs.forEach( run => {
				// Create a summary of each run
				const runSummary = [];

				// Add the run status
				runSummary.push( {
					label: strings.summary.status,
					value: html`
						<uap-log-status status-id="${ runGroup.status_id }"></uap-log-status>
					`
				} );

				// Add the date
				runSummary.push( {
					label: strings.summary.date,
					value: run.date
				} );

				// Check if there is an error message
				if ( ! isEmpty( run.result_message ) ) {
					runSummary.push( {
						label: strings.summary.message,
						value: htmlEntities.decode( run.result_message )
					} );
				}

				runs.push( {
					title: html`
						<uap-log-status status-id="${ runGroup.status_id }" hide-label></uap-log-status>
						${ run.run_identifier }
					`,
					summary: runSummary,
					properties: [], // Add run properties
				} );
			} );
		} );

		return runs;
	}

	/**
	 * The message shown when the action data is missing
	 *
	 * @return {String} The message
	 */
	get dataMissingErrorMessage() {
		return strings.missingItem;
	}
}
